import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../firebase/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiUrl } from "../../../config/api";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import ProductCompetitorView from "./tabs/products/ProductCompetitorView";
import UploadCompetitor from "./tabs/upload/UploadCompetitor";

function ProductCompetitor() {
    const { currentUser, getUserProfile, hasRole } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const profile = getUserProfile();

    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl);
    const [loading, setLoading] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);

    // Ref to abort in-flight search/filter requests when a newer one starts
    const searchAbortControllerRef = useRef(null);
    const [activeTab, setActiveTab] = useState(
        location.state?.activeTab || "database",
    );

    // Data state
    const [products, setProducts] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        per_page: 20,
        total: 0,
        pages: 1,
    });
    const [categories, setCategories] = useState([]);
    const [competitors, setCompetitors] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [competitorFilter, setCompetitorFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // ── Tab config ─────────────────────────────────────────────────────────────
    const TAB_DEFAULT = {
        database: {
            id: "database",
            iconPath:
                "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
            labelEn: "Products",
            labelZh: "競品庫",
        },
    };

    const TAB_CONFIG = useMemo(() => {
        if (hasRole && hasRole("competitor.create")) {
            return {
                ...TAB_DEFAULT,
                upload: {
                    id: "upload",
                    iconPath:
                        "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
                    labelEn: "Upload",
                    labelZh: "上傳",
                },
            };
        }
        return TAB_DEFAULT;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasRole]);

    // ── URL / auth effects ─────────────────────────────────────────────────────
    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    useEffect(() => {
        if (!(activeTab in TAB_CONFIG)) setActiveTab("database");
    }, [TAB_CONFIG, activeTab]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
    }, [language, location.search, navigate]);

    useEffect(() => {
        if (!currentUser)
            navigate({ pathname: "/login", search: `?lang=${language}` });
    }, [currentUser, navigate, language]);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchAll = async ({
        page = pagination.page,
        per_page = pagination.per_page,
        category_id = categoryFilter,
        producer_id = competitorFilter,
        q = searchQuery,
    } = {}) => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const params = new URLSearchParams({ page, per_page });
            if (category_id) params.set("category_id", category_id);
            if (producer_id) params.set("producer_id", producer_id);
            if (q) params.set("q", q);

            const [productsRes, categoriesRes, competitorsRes] =
                await Promise.allSettled([
                    axios.get(
                        `${getApiUrl()}/api/v2/competitor-products/?${params}`,
                        config,
                    ),
                    categories.length === 0
                        ? axios.get(
                              `${getApiUrl()}/api/v2/competitor-categories/`,
                              config,
                          )
                        : Promise.resolve(null),
                    competitors.length === 0
                        ? axios.get(
                              `${getApiUrl()}/api/v2/competitors/`,
                              config,
                          )
                        : Promise.resolve(null),
                ]);

            if (productsRes.status === "fulfilled") {
                const data = productsRes.value.data?.data ?? {};

                // Resolve competitor_string from competitors list (may not be loaded yet)
                const competitorList =
                    competitorsRes.status === "fulfilled" &&
                    competitorsRes.value
                        ? competitorsRes.value.data?.data || []
                        : competitors;

                const competitorMap = Object.fromEntries(
                    competitorList.map((c) => [
                        c.competitor_id,
                        c.competitor_string,
                    ]),
                );

                // Resolve competitor categories
                const categoryList =
                    categoriesRes.status === "fulfilled" && categoriesRes.value
                        ? categoriesRes.value.data?.data || []
                        : categories;

                const categoryMap = Object.fromEntries(
                    categoryList.map((cat) => [cat.category_id, cat]),
                );

                const enriched = (data.products || []).map((p) => ({
                    ...p,
                    competitor_string:
                        p.competitor_string ??
                        competitorMap[p.producer_id] ??
                        null,
                    category_name:
                        categoryMap[p.category_id]?.category_name || null,
                    sub_category_name:
                        categoryMap[p.category_id]?.sub_category || null,
                }));

                setProducts(enriched);

                console.log(enriched);
                if (data.pagination) setPagination(data.pagination);
            } else {
                setProducts([]);
            }

            if (categoriesRes.status === "fulfilled" && categoriesRes.value) {
                setCategories(categoriesRes.value.data?.data || []);
            }

            if (competitorsRes.status === "fulfilled" && competitorsRes.value) {
                setCompetitors(competitorsRes.value.data?.data || []);
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSearch = async ({
        page = 1,
        per_page = pagination.per_page,
        category_id = categoryFilter,
        producer_id = competitorFilter,
        q = searchQuery,
    } = {}) => {
        if (!currentUser) return;

        // Abort any previous in-flight request
        if (searchAbortControllerRef.current) {
            searchAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        searchAbortControllerRef.current = controller;

        setLoadingSearch(true);
        try {
            const token = await currentUser.getIdToken();
            const params = new URLSearchParams({ page, per_page });
            if (category_id) params.set("category_id", category_id);
            if (producer_id) params.set("producer_id", producer_id);
            if (q) params.set("q", q);
            const res = await axios.get(
                `${getApiUrl()}/api/v2/competitor-products/?${params}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                },
            );
            const data = res.data?.data ?? {};
            const competitorMap = Object.fromEntries(
                competitors.map((c) => [c.competitor_id, c.competitor_string]),
            );
            const categoryMap = Object.fromEntries(
                categories.map((cat) => [cat.category_id, cat]),
            );
            const enriched = (data.products || []).map((p) => ({
                ...p,
                competitor_string:
                    p.competitor_string ?? competitorMap[p.producer_id] ?? null,
                category_name:
                    p.category_name ?? categoryMap[p.category_id]?.category_name ?? null,
                sub_category_name:
                    p.sub_category_name ?? categoryMap[p.category_id]?.sub_category ?? null,
            }));
            setProducts(enriched);
            if (data.pagination) setPagination(data.pagination);
        } catch (err) {
            if (axios.isCancel(err) || err.name === "CanceledError") {
                // Request was aborted by a newer request — ignore silently
                return;
            }
            console.error("Search error:", err);
        } finally {
            // Only clear loading if this controller is still the latest one
            if (searchAbortControllerRef.current === controller) {
                setLoadingSearch(false);
            }
        }
    };

    // Initial load only. fetchAll reads (and writes) pagination, filters and
    // the competitor/category maps, so listing it here would refetch in a loop —
    // later fetches are triggered explicitly by the search/pagination handlers.
    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // ── Tab button ─────────────────────────────────────────────────────────────
    const TabButton = ({ tabKey, config }) => {
        const isActive = activeTab === tabKey;
        return (
            <button
                onClick={() => setActiveTab(tabKey)}
                className={`relative flex flex-col md:flex-row items-center justify-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 lg:px-6 py-2 md:py-3 rounded-xl transition-all duration-200 flex-1 min-w-0 ${
                    isActive
                        ? "bg-orange-500 text-white"
                        : "text-gray-600 hover:bg-slate-300"
                }`}
            >
                <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={config.iconPath}
                    />
                </svg>
                <span className="font-medium text-xs sm:text-sm truncate">
                    {language === "zh" ? config.labelZh : config.labelEn}
                </span>
            </button>
        );
    };

    // ── Loading screen ─────────────────────────────────────────────────────────
    if (!currentUser || !profile || loading) {
        return (
            <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
                <Sidebar />
                <LanguageSwitcher
                    language={language}
                    setLanguage={setLanguage}
                />
                <div className="flex-1 flex flex-col items-center justify-center -mt-20">
                    <div className="flex flex-col gap-4 items-center text-center">
                        <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                                {[0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                                        style={{
                                            animationDelay: `${i * 0.1}s`,
                                        }}
                                    />
                                ))}
                            </div>
                            <span className="text-orange-600 dark:text-orange-500 text-lg font-semibold">
                                {language === "zh"
                                    ? "正在加載數據..."
                                    : "Loading data..."}
                            </span>
                        </div>
                        <p className="text-sm text-orange-800 dark:text-orange-200">
                            {language === "zh"
                                ? "請稍候，我們正在為您處理繁重的工作。"
                                : "Please wait while we do the heavy lifting for you."}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans overflow-hidden">
            <Sidebar />
            <LanguageSwitcher language={language} setLanguage={setLanguage} />

            <div className="flex-1 flex flex-col overflow-hidden pt-12">
                <div className="flex-1 overflow-y-auto">
                    <div className="w-5/6 mx-auto px-4 lg:px-8 py-8">
                        {/* Page header */}
                        <div className="relative mb-8 lg:mb-12 text-center">
                            <h1 className="text-3xl lg:text-4xl font-bold text-black dark:text-white mb-2">
                                {language === "zh"
                                    ? "競爭對手洞察中心"
                                    : "Competitor Insights Center"}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                                {language === "zh"
                                    ? "瀏覽、搜索和管理競爭對手的產品信息"
                                    : "Browse, search, and manage competitor product information"}
                            </p>
                        </div>

                        {/* Tab navigation */}
                        <div className="flex justify-center mb-8 w-full">
                            <div className="inline-flex gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 bg-gray-100 shadow-xl rounded-xl w-full max-w-full overflow-x-auto">
                                {Object.entries(TAB_CONFIG).map(
                                    ([key, config]) => (
                                        <TabButton
                                            key={key}
                                            tabKey={key}
                                            config={config}
                                        />
                                    ),
                                )}
                            </div>
                        </div>

                        {/* Tab content */}
                        <div className="animate-fadeIn">
                            {activeTab === "database" && (
                                <div className="animate-slideUp">
                                    <ProductCompetitorView
                                        language={language}
                                        loading={loading}
                                        products={products}
                                        pagination={pagination}
                                        categories={categories}
                                        competitors={competitors}
                                        categoryFilter={categoryFilter}
                                        competitorFilter={competitorFilter}
                                        searchQuery={searchQuery}
                                        loadingSearch={loadingSearch}
                                        onCategoryChange={(id) => {
                                            setCategoryFilter(id);
                                            fetchSearch({
                                                page: 1,
                                                category_id: id,
                                            });
                                        }}
                                        onCompetitorChange={(id) => {
                                            setCompetitorFilter(id);
                                            fetchSearch({
                                                page: 1,
                                                producer_id: id,
                                            });
                                        }}
                                        onSearchChange={(val) => {
                                            setSearchQuery(val);
                                            fetchSearch({ page: 1, q: val });
                                        }}
                                        onPageChange={(p) =>
                                            fetchSearch({ page: p })
                                        }
                                        onProductDeleted={() =>
                                            fetchAll({ page: 1 })
                                        }
                                        onNavigateToUpload={() =>
                                            setActiveTab("upload")
                                        }
                                    />
                                </div>
                            )}
                            {activeTab === "upload" &&
                                "upload" in TAB_CONFIG && (
                                    <div className="animate-slideUp">
                                        <UploadCompetitor
                                            language={language}
                                            currentUser={currentUser}
                                        />
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductCompetitor;
